import cx from 'classnames';
import * as React from 'react';

import FormElement from 'components/FormElement';
import Input from 'components/Input';
import Tooltip from 'components/Tooltip';
import TooltipTrigger from 'components/TooltipTrigger';

import './styles/PasswordStrengthField.scss';

type Props = {
  name: string,
  label: string,
  onScoreUpdated?: (score: number, variant: string | null | void) => void,
  onPasswordUpdated?: (password: string) => void,
};

type State = {
  score: number,
  password: string,
  zxcvbn?: (password: string, userInputs?: string[] | undefined) => zxcvbn.ZXCVBNResult
};

const SCORE_MAPPINGS = [
  { description: 'Weak', level: 'weak' }, // 0
  { description: 'Weak', level: 'weak' }, // 1
  { description: 'Good', level: 'good' }, // 2
  { description: 'Good', level: 'good' }, // 3
  { description: 'Strong', level: 'strong' }, // 4
];

const UNSCORED = -1;
const MIN_LENGTH = 6;

export default class PasswordStrengthField extends React.Component<Props, State> {
  state: State = {
    password: '',
    score: UNSCORED,
    zxcvbn: undefined,
  };

  componentDidMount() {
    this.loadZXCVBN();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.onScoreUpdated) {
      if (this.state.score !== prevState.score) {
        this.props.onScoreUpdated(this.state.score);
      }
    }
  }

  loadZXCVBN() {
    // https://github.com/airbnb/babel-plugin-dynamic-import-node/issues/47
    // webpack and node resolve the dynamic module differently
    import(/* webpackChunkName: "zxcvbn" */ 'zxcvbn')
      .then(zxcvbn => this.setState({ zxcvbn: zxcvbn.default || zxcvbn }))
      .catch(e => console.error('password strength field: could not load zxcvbn library: ', e));
  }

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const password = event.target.value;
    const score = this.state.zxcvbn ? this.state.zxcvbn(password).score : UNSCORED;
    this.setState({ password, score });
    if (this.props.onPasswordUpdated) {
      this.props.onPasswordUpdated(password);
    }
  };

  render() {
    const { score, password } = this.state;
    const { name, label } = this.props;

    let level;
    let description;

    if (score > UNSCORED) {
      ({ level, description } = SCORE_MAPPINGS[score]);

      if (password.length < MIN_LENGTH) {
        description = 'Too short';
      }
    }

    const classnames = ['PasswordStrengthField'];

    if (password.length === 0) {
      classnames.push('PasswordStrengthField--empty');
    } else if (password.length < MIN_LENGTH) {
      classnames.push('PasswordStrengthField--score-too-short');
    } else if (level) {
      classnames.push(`PasswordStrengthField--score-${level}`);
    }

    return (
      <div className={cx(classnames)}>
        <FormElement
          label={label}
          onValidChange={this.handleChange}
          onInvalidChange={this.handleChange}
          data-test="PasswordStrengthField-input"
          validate={() => {
            if (score === UNSCORED) {
              // when scored, we show the validation message in the description.
              throw Error('Password must be at least 6 characters long.');
            }
          }}
          element={Input}
          elementProps={{
            required: true,
            type: 'password',
            autoComplete: 'new-password',
            className: 'PasswordStrengthField-input',
            name,
          }}
        />
        {score !== UNSCORED && (
          <React.Fragment>
            <div className="PasswordStrengthField-strength-bar" />
            <TooltipTrigger
              placement="top"
              tooltip={
                <Tooltip>
                  Good passwords are hard to guess. Use uncommon words or inside jokes, non-standard
                  uppercasing, creative spelling, and non-obvious numbers and symbols.
                </Tooltip>
              }>
              <span className="PasswordStrengthField-strength-desc">{description}</span>
            </TooltipTrigger>
          </React.Fragment>
        )}
      </div>
    );
  }
}
